import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  ExternalLink,
  FileText,
  Loader2,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { AgentConsole } from "@/components/intelligence/AgentConsole";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { MatchFactorBar } from "@/components/intelligence/MatchFactorBar";
import { RulesPanel } from "@/components/intelligence/RulesPanel";
import { ComparativaTable, type ComparativaRow } from "@/components/intelligence/CompanyVsTenderTable";
import { RiesgoFavoritoCard } from "@/components/oc/RiesgoFavoritoCard";
import { ScoreRing } from "@/components/intelligence/ScoreRing";
import { RecommendationBadge } from "@/components/intelligence/RecommendationBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { compraAgilIaService, type CompraAgilOpportunity } from "@/services/compra-agil-ia.service";
import { compraAgilService } from "@/services/compra-agil.service";
import { companyProfileService } from "@/services";
import { formatCLP } from "@/lib/mock-data";
import { levelLabel } from "@/lib/intelligence/scoring";
import { estadoCALabels, textoCierre } from "@/lib/compra-agil-signals";
import { cn } from "@/lib/utils";
import { mercadoPublicoUrl } from "@/lib/licitaciones";
import type { CompanyProfile } from "@/types";

export const Route = createFileRoute("/compra-agil-ia/$id")({
  head: () => ({ meta: [{ title: "Análisis de Compra Ágil — LicitaPro" }] }),
  component: CompraAgilIaDetalle,
});

function mensajeError(e: unknown): string {
  return e instanceof Error ? e.message : "Error desconocido";
}

/**
 * Comparativa perfil ↔ Compra Ágil. Los factores son los del mecanismo, no los
 * de una licitación: no hay modalidad ni certificaciones exigidas, y sí pesan
 * el plazo de entrega y cuántos ya cotizaron.
 */
function filasComparativa(
  profile: CompanyProfile,
  o: CompraAgilOpportunity,
): ComparativaRow[] {
  const ca = o.compraAgil;
  const productos = (ca.productosSolicitados ?? []).map((p) => p.nombre).filter(Boolean);
  const rubros = profile.datos.rubros;
  const region = ca.nombreRegion ?? "No informada";
  const monto = ca.montoDisponibleCLP;
  const cotizando = ca.totalOfertasRecibidas ?? 0;

  return [
    {
      factor: "Rubro / Productos",
      empresa: rubros.slice(0, 2).join(", ") || "No declarado",
      licitacion: productos.slice(0, 2).join(", ") || "Sin detalle sincronizado",
      match: productos.some((p) => rubros.some((r) => p.toLowerCase().includes(r.toLowerCase())))
        ? "ok"
        : "warn",
    },
    {
      factor: "Región de entrega",
      empresa: profile.estrategia.regionesPreferidas.join(", ") || "Sin preferencia",
      licitacion: region,
      match: profile.estrategia.regionesPreferidas.includes(region)
        ? "ok"
        : profile.datos.regiones.includes(region)
          ? "warn"
          : "fail",
    },
    {
      factor: "Presupuesto",
      empresa: `${formatCLP(profile.estrategia.presupuestoMin)} — ${formatCLP(profile.estrategia.presupuestoMax)}`,
      licitacion: monto != null ? formatCLP(monto) : "No informado",
      match:
        monto != null &&
        monto >= profile.estrategia.presupuestoMin &&
        monto <= profile.estrategia.presupuestoMax
          ? "ok"
          : "warn",
    },
    {
      factor: "Plazo de entrega",
      empresa: `Complejidad aceptada: ${profile.estrategia.complejidadAceptada}`,
      licitacion: ca.plazoEntregaDias != null ? `${ca.plazoEntregaDias} días` : "No informado",
      match: ca.plazoEntregaDias == null ? "warn" : ca.plazoEntregaDias >= 10 ? "ok" : "warn",
    },
    {
      factor: "Competencia",
      empresa: `Tolerancia al riesgo: ${profile.estrategia.toleranciaRiesgo}`,
      licitacion: cotizando === 0 ? "Sin cotizaciones aún" : `${cotizando} cotizando`,
      match: cotizando === 0 ? "ok" : cotizando <= 3 ? "warn" : "fail",
    },
    {
      factor: "Historial organismo",
      empresa: `${profile.historial.organismosFrecuentes.find((x) => x.nombre === ca.organismoComprador)?.adjudicadas ?? 0} adjudicaciones`,
      licitacion: ca.organismoComprador,
      match: profile.historial.organismosFrecuentes.some(
        (x) => x.nombre === ca.organismoComprador,
      )
        ? "ok"
        : "warn",
    },
  ];
}

function CompraAgilIaDetalle() {
  const { id } = Route.useParams();
  const [enCurso, setEnCurso] = useState<string[]>([]);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const qc = useQueryClient();

  const oportunidad = useQuery({
    queryKey: ["compra-agil-ia", "detalle", id],
    queryFn: () => compraAgilIaService.byId(id),
    retry: false,
  });
  const analisis = useQuery({
    queryKey: ["compra-agil-ia", "analisis", id],
    queryFn: () => compraAgilIaService.analisis(id),
    retry: false,
  });
  const perfil = useQuery({
    queryKey: ["company-profile"],
    queryFn: () => companyProfileService.get(),
    retry: false,
  });
  // El seguimiento vive en la ficha operativa; acá se lee para saber si ya
  // está en el pipeline y en qué etapa.
  const ficha = useQuery({
    queryKey: ["compra-agil", id],
    queryFn: () => compraAgilService.detalle(id),
    retry: false,
  });

  const evaluar = useMutation({
    mutationFn: () => compraAgilIaService.evaluar(id),
    onSuccess: (r) => {
      if (r.evaluadas === 0 && r.error) toast.error(r.error);
      else toast.success("Evaluación IA actualizada");
      void oportunidad.refetch();
    },
    onError: (e: Error) => toast.error(e.message || "No se pudo evaluar"),
  });

  /**
   * Acciones del copiloto. A diferencia de Licitaciones —donde los botones
   * equivalentes sólo escriben una señal en localStorage porque el módulo
   * `/learning` no existe en el backend— acá mueven la etapa real del
   * pipeline CRM, que para Compra Ágil sí está implementado.
   */
  const mover = useMutation({
    mutationFn: async (etapa: "preparacion" | "analisis" | "cerrada") => {
      if (!ficha.data?.tracking) await compraAgilService.track(id);
      return compraAgilService.updateTracking(id, { estadoPipeline: etapa });
    },
    onSuccess: (_r, etapa) => {
      toast.success(
        etapa === "preparacion"
          ? "Marcada para cotizar"
          : etapa === "analisis"
            ? "Movida a revisar"
            : "Descartada",
      );
      void qc.invalidateQueries({ queryKey: ["compra-agil", id] });
      void qc.invalidateQueries({ queryKey: ["crm"] });
    },
    onError: () => toast.error("No se pudo actualizar el pipeline"),
  });

  const correrAgente = async (agenteId: string) => {
    setEnCurso((v) => [...v, agenteId]);
    setErrores(({ [agenteId]: _quitado, ...resto }) => resto);
    try {
      await compraAgilIaService.analizar(id, agenteId);
      await analisis.refetch();
    } catch (e) {
      setErrores((v) => ({ ...v, [agenteId]: mensajeError(e) }));
    } finally {
      setEnCurso((v) => v.filter((a) => a !== agenteId));
    }
  };

  const correrTodos = async () => {
    for (const a of analisis.data ?? []) {
      if (a.activo) await correrAgente(a.agenteId);
    }
  };

  if (oportunidad.isLoading) {
    return (
      <AppShell title="Compra Ágil IA">
        <Skeleton className="h-32 rounded-xl mb-4" />
        <Skeleton className="h-64 rounded-xl" />
      </AppShell>
    );
  }

  const o = oportunidad.data;
  if (!o) {
    return (
      <AppShell title="Sin análisis">
        <EmptyState
          icon={AlertTriangle}
          title="Esta Compra Ágil no está en el embudo"
          description="Solo aparecen acá las que superan tu umbral. Baja el score mínimo o pulsa «Recalcular todo» en el listado."
          action={
            <Link to="/compra-agil-ia" className="text-sm text-primary hover:underline">
              Volver a Compra Ágil IA
            </Link>
          }
        />
      </AppShell>
    );
  }

  const { compraAgil: ca, scoring: s } = o;
  const cierre = textoCierre(ca.fechaCierre);

  return (
    <AppShell
      title={ca.nombre}
      subtitle={`${ca.codigo} · ${ca.organismoComprador}`}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => evaluar.mutate()}
            disabled={evaluar.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs text-primary hover:bg-primary/15 disabled:opacity-40"
          >
            <Sparkles className={cn("size-3.5", evaluar.isPending && "animate-spin")} />
            Reevaluar con IA
          </button>
          <Link
            to="/compra-agil/$id"
            params={{ id: ca.id }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <FileText className="size-4" /> Ver ficha
          </Link>
          {/* Publicación en Mercado Público: es donde están los adjuntos y el
              foro de consultas, que el análisis no alcanza a cubrir. */}
          <a
            href={mercadoPublicoUrl(ca.codigo)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="size-4" /> Publicación original
          </a>
          <Link
            to="/compra-agil-ia"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </div>
      }
    >
      <div className="mb-6 rounded-xl border border-border bg-card p-5 shadow-elegant">
        <div className="flex flex-wrap items-start gap-5">
          <ScoreRing score={s.score} label="MATCH" size={72} />
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <RecommendationBadge value={s.recommendation} size="sm" />
              {o.origen === "ia" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                  <Sparkles className="size-2.5" /> Evaluado con IA
                </span>
              )}
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {estadoCALabels[ca.estadoCodigo] ?? ca.estadoCodigo}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {s.probabilidad}% de probabilidad · Riesgo {levelLabel(s.riesgo)} · Competencia{" "}
              {levelLabel(s.competencia)} · Cierra {cierre.texto}
              {ca.montoDisponibleCLP != null && ` · ${formatCLP(ca.montoDisponibleCLP)}`}
            </p>
            {o.veredictoTexto && <p className="mt-3 text-sm text-foreground">{o.veredictoTexto}</p>}
          </div>
        </div>

        {(s.proRazones.length > 0 || s.contraRazones.length > 0) && (
          <div className="mt-4 grid gap-2 border-t border-border pt-4 text-xs text-muted-foreground sm:grid-cols-2">
            {s.proRazones.length > 0 && (
              <p>
                <span className="font-medium text-success">A favor:</span>{" "}
                {s.proRazones.join(" · ")}
              </p>
            )}
            {s.contraRazones.length > 0 && (
              <p>
                <span className="font-medium text-destructive">En contra:</span>{" "}
                {s.contraRazones.join(" · ")}
              </p>
            )}
          </div>
        )}

        {o.recomendaciones.length > 0 && (
          <ul className="mt-4 space-y-1 border-t border-border pt-4 text-sm text-muted-foreground">
            {o.recomendaciones.map((r, i) => (
              <li key={i}>· {r}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Por qué el score es el que es: peso y aporte de cada factor. */}
          {s.factors.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
              <h3 className="mb-3 flex items-center gap-2 font-display font-semibold">
                <BarChart3 className="size-4 text-primary" /> Factores del match
              </h3>
              <div className="space-y-3">
                {s.factors.map((f) => (
                  <MatchFactorBar key={f.key} factor={f} />
                ))}
              </div>
            </section>
          )}

          {perfil.data && (
            <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
              <h3 className="mb-3 flex items-center gap-2 font-display font-semibold">
                <Users className="size-4 text-primary" /> Tu empresa frente a esta Compra Ágil
              </h3>
              <ComparativaTable
                rows={filasComparativa(perfil.data, o)}
                columnaProceso="Compra Ágil"
              />
            </section>
          )}

          <RulesPanel reglas={o.reglasEvaluadas} />
        </div>

        <div className="space-y-4">
          {/* Acciones que sí mueven el pipeline, no señales locales. */}
          <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
            <h3 className="mb-3 flex items-center gap-2 font-display font-semibold">
              <Trophy className="size-4 text-primary" /> Acciones del copiloto
            </h3>
            {ficha.data?.tracking && (
              <p className="mb-3 text-xs text-muted-foreground">
                En pipeline · etapa actual:{" "}
                <span className="text-foreground">{ficha.data.tracking.estadoPipeline}</span>
              </p>
            )}
            <div className="space-y-2">
              <button
                onClick={() => mover.mutate("preparacion")}
                disabled={mover.isPending}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-gradient-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-90 disabled:opacity-60"
              >
                {mover.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trophy className="size-4" />}
                Marcar para cotizar
              </button>
              <button
                onClick={() => mover.mutate("analisis")}
                disabled={mover.isPending}
                className="w-full rounded-md border border-border px-3 py-2 text-sm font-medium transition hover:bg-muted disabled:opacity-60"
              >
                Mover a revisar
              </button>
              <button
                onClick={() => mover.mutate("cerrada")}
                disabled={mover.isPending}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition hover:bg-muted disabled:opacity-60"
              >
                Descartar
              </button>
            </div>
          </section>

          <RiesgoFavoritoCard organismo={ca.organismoComprador} />
        </div>
      </div>

      <AgentConsole
        agentes={analisis.data ?? []}
        enCurso={enCurso}
        errores={errores}
        cargando={analisis.isLoading}
        errorCarga={analisis.isError ? mensajeError(analisis.error) : null}
        onReintentar={() => void analisis.refetch()}
        onRun={(a) => void correrAgente(a)}
        onRunAll={() => void correrTodos()}
      />
    </AppShell>
  );
}
